/**
 * Listings service: list listings, publish to marketplace (MLB) and persist to listings table.
 */
import axios from 'axios';
import { authHeaders } from './apiAuth';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * Get all listings (for Update listing page dropdown).
 * @param {string} accessToken - User's JWT
 * @returns {Promise<{ success: boolean, listings?: Array<object>, error?: string }>}
 */
export const getListings = async (accessToken) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/listings`, {
      headers: authHeaders(accessToken)
    });
    return response.data;
  } catch (error) {
    const detail = error.response?.data?.detail;
    const message = typeof detail === 'string' ? detail : detail?.message || 'Failed to get listings';
    throw new Error(message);
  }
};

/**
 * Publish a product to Mercado Livre and save the listing to our DB.
 * Backend sends mlb_item to MLB, creates a listing row, then creates listings_products_suppliers rows from listing_supplier_entries.
 *
 * @param {string} accessToken - User's JWT
 * @param {Object} params
 * @param {number} params.product_id - Our product id (FK)
 * @param {Object} params.mlb_item - Payload for MLB POST /items (title, category_id, price, sale_terms, pictures, attributes, description, etc.)
 * @param {Array<{ products_suppliers_id: number, selected_quantity: number, priority?: number }>} params.listing_supplier_entries - At least one entry; each creates a listings_products_suppliers row with allocated_amount = selected_quantity and priority.
 * @returns {Promise<{ success: boolean, listing?: Object, listing_product_suppliers?: Array, mlb_item?: Object, error?: string }>}
 */
export const publishListing = async (accessToken, { product_id, mlb_item, listing_supplier_entries }) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/listings/publish`,
      { product_id, mlb_item, listing_supplier_entries },
      { headers: authHeaders(accessToken) }
    );
    return response.data;
  } catch (error) {
    const detail = error.response?.data?.detail;
    const message =
      typeof detail === 'string'
        ? detail
        : Array.isArray(detail) && detail[0]?.msg != null
          ? detail.map((d) => d.msg || d.message).filter(Boolean).join('; ') || 'Failed to publish listing'
          : detail?.message || 'Failed to publish listing';
    throw new Error(message || 'Failed to publish listing');
  }
};
